import { Car, Cog, Droplets, Factory, Snowflake, Thermometer, Wind, Zap, type LucideIcon } from 'lucide-react';
import { Scope1ActivityType, Scope2ActivityType } from '../types/emissions';

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  [Scope1ActivityType.StationaryCombustion]: 'Stationary Combustion',
  [Scope1ActivityType.MobileCombustion]: 'Mobile Combustion',
  [Scope1ActivityType.ProcessEmissions]: 'Process Emissions',
  [Scope1ActivityType.FugitiveEmissions]: 'Fugitive Emissions',
  [Scope2ActivityType.PurchasedElectricity]: 'Purchased Electricity',
  [Scope2ActivityType.PurchasedSteam]: 'Purchased Steam',
  [Scope2ActivityType.PurchasedHeating]: 'Purchased Heating',
  [Scope2ActivityType.PurchasedCooling]: 'Purchased Cooling',
};

interface ActivityTypeBrand {
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  /** Tailwind border-color token consumed by MetricCard.borderColor (e.g. 'orange-500'). */
  borderColor: string;
}

/**
 * Lucide-icon + Tailwind-token branding per activity type. Mirrors
 * `assetTypeBranding.ts` and `SCOPE_TAILWIND` so KPI strips and other
 * MetricCard surfaces can render the activity type with the same
 * vocabulary the rest of the app uses.
 */
export const ACTIVITY_TYPE_BRANDING: Record<string, ActivityTypeBrand> = {
  [Scope1ActivityType.StationaryCombustion]: {
    Icon: Factory,
    iconBg: 'tw:bg-orange-500/10',
    iconColor: 'tw:text-orange-600',
    borderColor: 'orange-500',
  },
  [Scope1ActivityType.MobileCombustion]: {
    Icon: Car,
    iconBg: 'tw:bg-orange-400/10',
    iconColor: 'tw:text-orange-500',
    borderColor: 'orange-400',
  },
  [Scope1ActivityType.ProcessEmissions]: {
    Icon: Cog,
    iconBg: 'tw:bg-amber-500/10',
    iconColor: 'tw:text-amber-600',
    borderColor: 'amber-500',
  },
  [Scope1ActivityType.FugitiveEmissions]: {
    Icon: Wind,
    iconBg: 'tw:bg-amber-400/10',
    iconColor: 'tw:text-amber-500',
    borderColor: 'amber-300',
  },
  [Scope2ActivityType.PurchasedElectricity]: {
    Icon: Zap,
    iconBg: 'tw:bg-yellow-500/10',
    iconColor: 'tw:text-yellow-500',
    borderColor: 'yellow-500',
  },
  [Scope2ActivityType.PurchasedHeating]: {
    Icon: Thermometer,
    iconBg: 'tw:bg-rose-500/10',
    iconColor: 'tw:text-rose-500',
    borderColor: 'rose-500',
  },
  [Scope2ActivityType.PurchasedSteam]: {
    Icon: Droplets,
    iconBg: 'tw:bg-sky-500/10',
    iconColor: 'tw:text-sky-500',
    borderColor: 'sky-500',
  },
  [Scope2ActivityType.PurchasedCooling]: {
    Icon: Snowflake,
    iconBg: 'tw:bg-blue-500/10',
    iconColor: 'tw:text-blue-500',
    borderColor: 'blue-500',
  },
};

export const ACTIVITY_TYPE_COLORS: Record<string, string> = {
  [Scope1ActivityType.StationaryCombustion]: '#e95e0f',
  [Scope1ActivityType.MobileCombustion]: '#f97316',
  [Scope1ActivityType.ProcessEmissions]: '#fb923c',
  [Scope1ActivityType.FugitiveEmissions]: '#fdab5c',
  [Scope2ActivityType.PurchasedElectricity]: '#f59e0b',
  [Scope2ActivityType.PurchasedHeating]: '#fbbf24',
  [Scope2ActivityType.PurchasedSteam]: '#fcd34d',
  [Scope2ActivityType.PurchasedCooling]: '#fde68a',
};

export const ACTIVITY_TYPE_FA_ICONS: Record<string, string> = {
  [Scope1ActivityType.StationaryCombustion]: 'fa-industry',
  [Scope1ActivityType.MobileCombustion]: 'fa-car',
  [Scope1ActivityType.ProcessEmissions]: 'fa-cogs',
  [Scope1ActivityType.FugitiveEmissions]: 'fa-wind',
  [Scope2ActivityType.PurchasedElectricity]: 'fa-bolt',
  [Scope2ActivityType.PurchasedHeating]: 'fa-fire',
  [Scope2ActivityType.PurchasedSteam]: 'fa-tint',
  [Scope2ActivityType.PurchasedCooling]: 'fa-snowflake',
};

export const formatActivityType = (type: string): string =>
  ACTIVITY_TYPE_LABELS[type] ?? type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
